import { Button, Select, Table, Text } from '@pentabd/ui';

import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import * as yup from 'yup';
import { useConstituencyWiseResultsType } from '@hooks/useConstituencyWiseResults';
import { useTranslation } from 'react-i18next';
import { useDivisions } from '@hooks/useDivisions';
import { allTimeSlots, autoSlotSelector, divisionMapper } from '@utils/utils';
import { useEffect } from 'react';
import { useCustomParams } from '@hooks/useCustomParams';
import {
  HeaderExtensionCenterWiseResult,
  constituencyWiseResultColumn,
} from '../constants';

export const resultsSearchValidation = yup.object().shape({
  division: yup.string(),
  timeSlot: yup.string(),
});

export type ResultsSearchFormDataType = yup.InferType<
  typeof resultsSearchValidation
>;

export function ConstituencyWiseResultsTable({
  constituencyWiseResults,
  isLoading,
  query,
  handleQuery,
  multipleQuery,
}: useConstituencyWiseResultsType) {
  const { divisionList, isLoading: divisionLoading } = useDivisions();

  const { t } = useTranslation();
  const { searchParams } = useCustomParams();
  const election_schedule_id = searchParams.get('election_schedule_id') || '';

  const methods = useForm({
    values: {
      division: '',
      timeSlot: autoSlotSelector().showSlotRange,
    },
  });

  const { handleSubmit, register, control, setValue } = methods;

  const onSubmit: SubmitHandler<ResultsSearchFormDataType> = (data: any) => {
    multipleQuery(data);
  };

  useEffect(() => {
    const allowDivision = divisionList[0]
      ? divisionList[0]
      : {
          division: '',
          id: '',
        };

    if (allowDivision?.id) {
      setValue('division', `${allowDivision.id}`);
      handleQuery('division', `${allowDivision.id}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divisionList]);

  return (
    <div className="py-10">
      <div className="bg-light p-10 mb-10 rounded">
        <FormProvider {...methods}>
          <form className="container" onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-sm-12 col-md-6 col-lg-3">
                <Text
                  weight="semibold"
                  size="md"
                  color="title"
                  component="p"
                  className="mb-5"
                >
                  {t(
                    'CONSTITUENCY_WISE_RESULTS.BODY.SEARCH_FORM.DIVISION.LABEL',
                  )}
                </Text>

                <Controller
                  control={control}
                  name={'division'}
                  render={({ field }) => {
                    return (
                      <Select
                        {...register(`division`)}
                        options={divisionMapper(divisionList)}
                        defaultValue={field.value as string}
                        placeholder={t(
                          'CONSTITUENCY_WISE_RESULTS.BODY.SEARCH_FORM.DIVISION.PLACEHOLDER',
                        )}
                        onSelectItem={(e) => {
                          setValue('division', e as string);
                        }}
                        disabled={divisionLoading || isLoading}
                        portal={true}
                      />
                    );
                  }}
                />
              </div>
              <div className="col-sm-12 col-md-6 col-lg-3">
                <Text
                  weight="semibold"
                  size="md"
                  color="title"
                  component="p"
                  className="mb-5"
                >
                  {t(
                    'CONSTITUENCY_WISE_RESULTS.BODY.SEARCH_FORM.TIME_SLOT.LABEL',
                  )}
                </Text>

                <Controller
                  control={control}
                  name={'timeSlot'}
                  render={({ field }) => {
                    return (
                      <Select
                        {...register(`timeSlot`)}
                        options={allTimeSlots}
                        defaultValue={field.value as string}
                        placeholder={t(
                          'CONSTITUENCY_WISE_RESULTS.BODY.SEARCH_FORM.TIME_SLOT.PLACEHOLDER',
                        )}
                        onSelectItem={(e) => {
                          setValue('timeSlot', e as string);
                        }}
                        disabled={isLoading}
                        portal={true}
                      />
                    );
                  }}
                />
              </div>

              <div className="col-sm-12 col-md-6 col-lg-3 align-self-end">
                <Button
                  fill="fill"
                  size="md"
                  type="info"
                  htmlType="submit"
                  isPadding={false}
                  className="px-20 py-5"
                  disabled={isLoading}
                >
                  {t(
                    'CONSTITUENCY_WISE_RESULTS.BODY.SEARCH_FORM.SEARCH_BUTTON.LABEL',
                  )}
                </Button>
              </div>
            </div>
          </form>
        </FormProvider>
      </div>

      <Table
        headerExtension={HeaderExtensionCenterWiseResult({
          isLoading,
          constituencyWiseResults,
          handleQuery,
          query,
        })}
        loading={isLoading}
        loadingItemCount={10}
        columns={constituencyWiseResultColumn({ t, election_schedule_id })}
        rows={constituencyWiseResults}
      />
    </div>
  );
}
